import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "src/services/commonLanguageHeader";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";

export type TirePresetSortDirection = "ASC" | "DESC";

export interface TirePresetListParams {
  page?: number;
  size?: number;
  sort?: TirePresetSortDirection;
  sortBy?: string;
}

export interface TirePresetRecord {
  id: string | number;
  name?: string;
  description?: string;
  active?: boolean | number | string;
  configuration?: TirePresetConfiguration;
  vehicleId?: string | number;
  vehicleTypeId?: string | number;
  isBase?: boolean;
  [key: string]: unknown;
}

export interface TirePresetPayload {
  configuration: TirePresetConfiguration;
  isBase: boolean;
  name: string;
  vehicleId: string | number;
  vehicleTypeId: string | number;
}

export interface TirePresetConfigurationItem {
  axle?: string | number;
  id?: string | number;
  position?: string;
  temperature?: string | number;
  pressure?: string | number;
  tireKilometers?: string | number;
  tireMake?: {
    id?: string | number;
    name?: string;
  };
  tireMeasure?: {
    id?: string | number;
    name?: string;
  };
  tireModel?: {
    id?: string | number;
    name?: string;
  };
  vehicleId?: string | number;
  vehicleKilometersAtInstall?: string | number;
  [key: string]: unknown;
}

export interface TirePresetAxisConfiguration {
  tires: string[];
  details: Record<string, TirePresetConfigurationItem>;
  distance: string | number;
}

export type TirePresetConfiguration = Record<string, TirePresetAxisConfiguration>;

export interface TirePresetStatusPayload {
  id: string | number;
  active: boolean;
}

export interface TirePresetPaginatedResponse {
  resources?: TirePresetRecord[];
  tirePresets?: TirePresetRecord[];
  data?: TirePresetRecord[];
  items?: TirePresetRecord[];
  content?: TirePresetRecord[];
  totalItems?: number;
  totalElements?: number;
  total?: number;
}

const replaceId = (url: string, id: string | number) => url.replace("{id}", String(id));

export const getTirePresetsList = (params: TirePresetListParams = {}, language?: string) =>
  ApiService.get<{ response: TirePresetPaginatedResponse }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    }
  );

export const getTirePresetById = (id: string | number, language?: string) =>
  ApiService.get<{ response: TirePresetRecord }>(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.GET, id),
    getLanguageHeaders(language)
  );

export const createTirePreset = (payload: TirePresetPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.ADD,
    payload,
    getLanguageHeaders(language)
  );

export const updateTirePreset = (
  id: string | number,
  payload: TirePresetPayload & { id?: string | number },
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.UPDATE, id),
    payload,
    getLanguageHeaders(language)
  );

export const updateTirePresetStatus = (
  id: string | number,
  payload: TirePresetStatusPayload,
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.UPDATE, id),
    payload,
    getLanguageHeaders(language)
  );

export const deleteTirePreset = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.TIRE_PRESET.DELETE, id),
    getLanguageHeaders(language)
  );
